import { useEffect, useState } from "react";

/**
 * Mount gates for the always-present global UI in `__root.tsx`.
 *
 * `lazy()` alone does NOT defer anything when the component is rendered
 * unconditionally: React resolves the dynamic import as soon as it renders, so
 * the chunk lands on the critical path of every page load. These hooks return
 * `false` on the server and on the first client render, so a gated component is
 * only rendered — and only then fetched — once it can actually be useful.
 */

/** Non-blocking "the page has settled" signal: window load, idle, or first input. */
export function useIdleMount(): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    const go = () => {
      if (cancelled) return;
      cancelled = true;
      cleanups.forEach((fn) => fn());
      setReady(true);
    };

    const onWindow = (event: string) => {
      window.addEventListener(event, go, { once: true, passive: true });
      cleanups.push(() => window.removeEventListener(event, go));
    };

    if (document.readyState === "complete") {
      // Already loaded (e.g. a client-side navigation): wait for idle only.
      const idle = window.requestIdleCallback?.(go, { timeout: 2000 });
      if (idle === undefined) {
        const t = window.setTimeout(go, 200);
        cleanups.push(() => window.clearTimeout(t));
      } else {
        cleanups.push(() => window.cancelIdleCallback?.(idle));
      }
    } else {
      onWindow("load");
    }

    // Any real interaction promotes the gate immediately.
    onWindow("pointerdown");
    onWindow("keydown");

    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return ready;
}

/**
 * Mount only after a window event fires. Used for the coach dock so its chunk
 * (and the coach artwork inside it) is fetched on the first "open coach" click
 * instead of on every page load, including the sign-in screen.
 */
export function useEventMount(eventName: string): boolean {
  const [fired, setFired] = useState(false);

  useEffect(() => {
    if (fired) return;
    const onFire = () => setFired(true);
    window.addEventListener(eventName, onFire);
    return () => window.removeEventListener(eventName, onFire);
  }, [eventName, fired]);

  return fired;
}
